/**
 * Public Voice-of-X report viewer — ``/public/voice-report/$token`` (NO auth).
 *
 * Landing page for a Voice-of-X public share-link. The visitor has no Ando
 * session — the chrome-less layout (cloned from
 * ``connect.invite.$token.tsx`` / ``connect.space.$spaceId.tsx``) mirrors
 * the standalone surfaces we expose to external audiences.
 *
 * Flow:
 *   1. The TanStack Query resolves ``getPublicVoiceReportShare(token)`` on
 *      mount (no credentials sent). On 200 → the BE returns a short-lived
 *      MinIO presigned URL for the ReportNative JSON.
 *   2. We then ``fetch()`` that URL client-side and render with the
 *      read-only ``ReportViewer`` (when the feature exists). For M3.2 we
 *      ship a minimal preview surface that confirms the link works and
 *      flags TODO M3.3 for plugging in the actual viewer.
 *   3. 404 / 410 / 403 / 429 → calm explanatory notice — identical
 *      vocabulary to the ``ConnectPublicNotice`` cards (notfound / expired
 *      / gone / rate_limited / forbidden).
 *
 * Accessibility / a11y:
 *   - Lingui ``<Trans>`` on every visible string.
 *   - Heading auto-focuses on mount via ``ConnectFocusHeading``.
 *   - Watermark + "scade fra…" chip live in the same ``aria-live="polite"``
 *     region so SR users hear the freshness of the snapshot.
 */

import {
  ConnectFocusHeading,
  ConnectPublicLoading,
  ConnectPublicNotice,
  ConnectPublicPage,
} from "@/features/connect";
import {
  type PublicVoiceReportShareResponse,
  VoiceReportShareError,
  getPublicVoiceReportShare,
} from "@ando/core";
import { cn } from "@ando/ui";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Clock, FileText, ShieldCheck } from "lucide-react";
import * as React from "react";

export const Route = createFileRoute("/public/voice-report/$token")({
  component: PublicVoiceReportRoute,
});

function reasonOf(err: unknown): VoiceReportShareError | null {
  return err instanceof VoiceReportShareError ? err : null;
}

function PublicVoiceReportRoute(): React.JSX.Element {
  const { i18n } = useLingui();
  const { token } = Route.useParams();

  // ``viewer_email`` — the FE could prompt for it on a 403 in a follow-up;
  // for M3.2 we read it from a ``?viewer_email=`` query param if present.
  const viewerEmail = React.useMemo(() => {
    if (typeof window === "undefined") return undefined;
    const sp = new URLSearchParams(window.location.search);
    return sp.get("viewer_email") || undefined;
  }, []);

  const query = useQuery<
    PublicVoiceReportShareResponse,
    VoiceReportShareError
  >({
    queryKey: ["public-voice-report-share", token, viewerEmail ?? null],
    queryFn: () => getPublicVoiceReportShare(token, viewerEmail),
    retry: false,
  });

  if (query.isLoading) {
    return <ConnectPublicLoading />;
  }

  // ── Error surfaces ──────────────────────────────────────────────────
  const err = reasonOf(query.error);
  if (query.isError && err) {
    return <ShareFailure error={err} />;
  }

  const data = query.data;
  if (!data) {
    return (
      <ConnectPublicNotice
        reason="notfound"
        title={<Trans>Link non trovato</Trans>}
        body={
          <Trans>
            Questo link di condivisione non esiste più. Chiedi a chi te l'ha
            inviato un nuovo collegamento.
          </Trans>
        }
      />
    );
  }

  return (
    <ConnectPublicPage label={i18n._(msg`Report condiviso — Ando`)}>
      <div className="m-auto w-full max-w-3xl">
        <div
          className={cn(
            "flex flex-col gap-5 rounded-[18px] border border-hairline bg-white/70 px-6 py-7 backdrop-blur-xl",
            "shadow-[0_1px_2px_rgba(0,0,0,0.04),inset_0_1px_0_rgba(255,255,255,0.6)] motion-safe:animate-surface-in",
          )}
        >
          <header className="flex flex-col gap-3">
            <span
              role="status"
              aria-live="polite"
              className="inline-flex w-fit items-center gap-1.5 rounded-full bg-cream-deep/70 px-2.5 py-1 font-medium text-[11.5px] text-ink-2"
            >
              <Clock size={11} strokeWidth={2.1} aria-hidden />
              <Trans>
                Anteprima condivisa — scade{" "}
                {new Date(data.expires_at).toLocaleString(i18n.locale || "it-IT", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </Trans>
            </span>
            <ConnectFocusHeading className="text-[24px]">
              {data.report_title}
            </ConnectFocusHeading>
            <p className="text-[12.5px] text-ink-2 leading-relaxed">
              <Trans>
                Stai visualizzando una versione di sola lettura del report. Le
                modifiche non sono possibili e le fonti restano dentro al
                workspace originale.
              </Trans>
            </p>
          </header>

          {/* TODO M3.3 — replace this surface with the read-only
              ``ReportViewer`` once it accepts a ``readOnly={true}`` prop.
              For M3.2 we surface the JSON download link so the link is
              demonstrably working end-to-end. */}
          <ReadOnlyReportPreview data={data} />

          <footer className="flex items-center justify-between gap-3 border-hairline border-t pt-3">
            <span className="inline-flex items-center gap-1.5 text-[11.5px] text-ink-3">
              <ShieldCheck size={12} strokeWidth={2.1} aria-hidden />
              <Trans>Generato da Ando · accesso in sola lettura</Trans>
            </span>
            <span className="text-[11.5px] text-ink-3" aria-hidden>
              ando.ai
            </span>
          </footer>
        </div>
      </div>
    </ConnectPublicPage>
  );
}

function ReadOnlyReportPreview({
  data,
}: {
  readonly data: PublicVoiceReportShareResponse;
}): React.JSX.Element {
  const { i18n } = useLingui();
  const [payload, setPayload] = React.useState<unknown>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(data.download_url, {
          method: "GET",
          headers: { Accept: "application/json" },
        });
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const json = (await res.json()) as unknown;
        if (!cancelled) setPayload(json);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data.download_url]);

  if (error !== null) {
    return (
      <div
        role="alert"
        className="rounded-md border border-rose-200 bg-rose-50/70 p-3 text-[12.5px] text-rose-800"
      >
        <Trans>
          Impossibile caricare il contenuto del report. Il link al payload è
          scaduto: aggiorna la pagina per richiedere un nuovo URL firmato.
        </Trans>
      </div>
    );
  }

  if (payload === null) {
    return (
      <div
        aria-busy="true"
        aria-live="polite"
        className="rounded-md border border-hairline bg-cream-deep/50 p-3 text-[12.5px] text-ink-3"
      >
        <Trans>Caricamento del contenuto del report…</Trans>
      </div>
    );
  }

  // Compact preview: title sections / executive summary if present. The
  // full ReportNative renderer plugs in at M3.3.
  const obj = (payload ?? {}) as Record<string, unknown>;
  const sections = Array.isArray(obj.sections) ? obj.sections : [];
  const execSummary =
    typeof obj.executive_summary === "string" ? obj.executive_summary : null;

  return (
    <article className="flex flex-col gap-3">
      {execSummary ? (
        <section>
          <h2 className="mb-1 font-semibold text-[13px] text-ink-1">
            <Trans>Sintesi</Trans>
          </h2>
          <p className="text-[13px] text-ink-1 leading-relaxed">{execSummary}</p>
        </section>
      ) : null}
      {sections.length > 0 ? (
        <ol className="flex flex-col gap-2">
          {sections.slice(0, 8).map((s, i) => {
            const so = (s ?? {}) as Record<string, unknown>;
            const sectionNumber = i + 1;
            const title =
              typeof so.title === "string" ? so.title : i18n._(msg`Sezione ${sectionNumber}`);
            const summary =
              typeof so.summary === "string" ? so.summary : null;
            return (
              <li
                key={i}
                className="flex flex-col gap-1 rounded-md border border-hairline bg-white/55 p-3"
              >
                <span className="inline-flex items-center gap-1.5 font-medium text-[12.5px] text-ink-1">
                  <FileText size={12} strokeWidth={2.1} aria-hidden />
                  {title}
                </span>
                {summary ? (
                  <p className="text-[12.5px] text-ink-2 leading-relaxed">
                    {summary}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : (
        <p className="text-[12.5px] text-ink-3">
          <Trans>Anteprima non disponibile per questo report.</Trans>
        </p>
      )}
    </article>
  );
}

function ShareFailure({
  error,
}: {
  readonly error: VoiceReportShareError;
}): React.JSX.Element {
  if (error.reason === "expired") {
    return (
      <ConnectPublicNotice
        reason="expired"
        title={<Trans>Link scaduto</Trans>}
        body={
          <Trans>
            Questo link di condivisione è scaduto. Chiedi a chi te l'ha
            inviato un nuovo collegamento.
          </Trans>
        }
      />
    );
  }
  if (error.reason === "revoked") {
    return (
      <ConnectPublicNotice
        reason="gone"
        title={<Trans>Link revocato</Trans>}
        body={
          <Trans>
            Questo link non è più valido. Chiedi a chi te l'ha inviato un
            nuovo collegamento.
          </Trans>
        }
      />
    );
  }
  if (error.reason === "forbidden") {
    return (
      <ConnectPublicNotice
        reason="gone"
        title={<Trans>Accesso non autorizzato</Trans>}
        body={
          <Trans>
            Il tuo indirizzo email non è autorizzato ad aprire questo link.
            Chiedi a chi te l'ha inviato di aggiungerti.
          </Trans>
        }
      />
    );
  }
  if (error.reason === "rate_limited") {
    return (
      <ConnectPublicNotice
        reason="rate_limited"
        title={<Trans>Troppe richieste</Trans>}
        body={
          <Trans>
            Hai effettuato troppe richieste. Attendi qualche istante e
            riprova.
          </Trans>
        }
      />
    );
  }
  if (error.reason === "notfound") {
    return (
      <ConnectPublicNotice
        reason="notfound"
        title={<Trans>Link non trovato</Trans>}
        body={
          <Trans>
            Questo link di condivisione non esiste. Verifica il collegamento o
            chiedine uno nuovo.
          </Trans>
        }
      />
    );
  }
  return (
    <ConnectPublicNotice
      reason="error"
      title={<Trans>Qualcosa è andato storto</Trans>}
      body={
        <Trans>
          Non siamo riusciti ad aprire il report. Riprova tra poco.
        </Trans>
      }
    />
  );
}
